import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

/**
 * Z13 — Tenant360 Legal / Jurisdiction Policy panel. The tenant's SECOND governed dimension.
 *
 * INDEPENDENT OF THE INDUSTRY PROFILE, and the panel is built to make that visible: it shows the
 * industry pin read-only beside its own, and applying here never writes it. The Industry Profile
 * decides WHICH retention classes apply to this business; the legal policy decides WHAT value
 * applies in its jurisdiction. Neither substitutes for the other.
 *
 * THIS PANEL ASSIGNS, IT NEVER AUTHORS. There is no field here for typing a retention value, a legal
 * source or a review status — those belong to the Legal / Jurisdiction Policies surface. Tenant360
 * chooses a published policy version and records the custodian's acknowledgement; that is all.
 *
 * IT RENDERS THE BACKEND'S ANSWER. Preview, the publish/review provenance and the post-Apply state
 * all come from the canonical routes verbatim, and Apply is followed by a canonical RE-READ rather
 * than by trusting local state — a frontend that congratulates itself without re-reading is how a
 * failed assignment looks successful.
 *
 * RELEASE ORDER IS RESPECTED, NOT BYPASSED, AND NEVER RE-DERIVED HERE. Every readiness fact —
 * catalogue sync, release state, usable, customer_visible, enforcement, operational readiness — is
 * READ from the backend projection. This panel contains no mapping from a release-state string to a
 * meaning, because a second place deciding what `internal_testing` means is a second governance
 * authority. The first version of this section stated that posture in fixed copy; the moment the
 * catalogue was actually synced that sentence became untrue and nothing in the UI could notice.
 */
const Tenant360LegalPolicy = ({ api, tenantRef, capabilityFeature = null }) => {
  const [policies, setPolicies] = useState([]);
  const [assignment, setAssignment] = useState(null);   // canonical persisted state
  const [selected, setSelected] = useState('');         // "policy_key@version"
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [confirmedBy, setConfirmedBy] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);

  const arr = (v) => (Array.isArray(v) ? v : []);
  const enc = encodeURIComponent;

  // The canonical re-read. Everything this panel claims about current state comes from here.
  const loadAssignment = useCallback(async () => {
    try {
      const r = await api.get(`/api/v1/superadmin/legal-policies/tenant/${enc(tenantRef)}/assignment`);
      setAssignment(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not read the tenant legal-policy assignment');
    }
  }, [api, tenantRef]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get('/api/v1/superadmin/legal-policies?active_only=true');
        if (alive) setPolicies(arr(r.data));
      } catch { if (alive) setPolicies([]); }
      if (alive) await loadAssignment();
    })();
    return () => { alive = false; };
  }, [api, loadAssignment]);

  const split = (v) => {
    const i = String(v).lastIndexOf('@');
    return i < 0 ? [v, null] : [String(v).slice(0, i), parseInt(String(v).slice(i + 1), 10)];
  };

  const doPreview = async () => {
    if (!selected) return;
    setBusy(true); setErr(null); setResult(null);
    const [policy_key, version] = split(selected);
    try {
      const r = await api.post('/api/v1/superadmin/legal-policies/assign/preview',
        { tenant_key: tenantRef, policy_key, version });
      setPreview(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Preview failed');
      setPreview(null);
    } finally { setBusy(false); }
  };

  const doApply = async () => {
    if (!selected || !confirmed) return;
    setBusy(true); setErr(null);
    const [policy_key, version] = split(selected);
    try {
      const r = await api.post('/api/v1/superadmin/legal-policies/assign/apply', {
        tenant_key: tenantRef, policy_key, version,
        applicability_confirmed: true,
        confirmed_by: confirmedBy || undefined,
        effective_from: effectiveFrom || undefined,
      });
      setResult(r.data);
      await loadAssignment();          // canonical re-read — never trust the local echo
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : (JSON.stringify(d) || 'Apply failed'));
    } finally { setBusy(false); }
  };

  const a = assignment || {};
  // THE canonical feature row, handed down from Tenant360's existing /threesixty/features load.
  // Not fetched here, not cached here, not recomputed here.
  const cap = capabilityFeature || {};
  const capKnown = !!capabilityFeature;
  const delta = preview?.retention_value_delta;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Legal / Jurisdiction Policy</CardTitle>
        <CardDescription>
          The tenant&apos;s second governed dimension, assigned independently of its Industry Profile.
          Retention values, legal sources and review status are authored on the Legal / Jurisdiction
          Policies surface — this panel only assigns a published version and records the custodian&apos;s
          applicability confirmation.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">

        {/* ── THE TWO DIMENSIONS, SIDE BY SIDE ───────────────────────────────────────────────── */}
        <section className="grid md:grid-cols-2 gap-3">
          <div className="border rounded p-3 bg-gray-50">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Industry Profile</div>
            <div className="font-mono text-sm">{a.industry_profile_key || '—'}</div>
            <div className="text-sm text-gray-600">
              version {a.industry_profile_version ?? '—'}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Read-only here. Assigning a legal policy never changes this.
            </div>
          </div>
          <div className="border rounded p-3">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Legal policy</div>
            <div className="font-mono text-sm">{a.legal_policy_key || '— not assigned —'}</div>
            <div className="text-sm text-gray-600">version {a.legal_policy_version ?? '—'}</div>
            <div className="flex flex-wrap gap-2 mt-2">
              {a.lifecycle && <Badge variant={a.lifecycle === 'active' ? 'default' : 'outline'}>{a.lifecycle}</Badge>}
              {a.review_status && <Badge variant="outline">review: {a.review_status}</Badge>}
              <Badge variant={a.applicability_confirmed ? 'default' : 'destructive'}>
                {a.applicability_confirmed ? 'applicability confirmed' : 'applicability NOT confirmed'}
              </Badge>
            </div>
            {a.reviewed_by && (
              <div className="text-xs text-gray-500 mt-2">
                Reviewed by {a.reviewed_by} ({a.reviewer_role}, {a.reviewer_organization})
                {a.published_by ? ` · published by ${a.published_by}` : ''}
              </div>
            )}
            {a.confirmed_by && (
              <div className="text-xs text-gray-500">
                Confirmed by {a.confirmed_by} at {a.confirmed_at}
                {a.effective_from ? ` · effective from ${a.effective_from}` : ''}
              </div>
            )}
          </div>
        </section>

        {/* ── EFFECTIVE GOVERNANCE ───────────────────────────────────────────────────────────── */}
        <section className="border rounded p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Effective governance</div>
          <ul className="text-sm space-y-1">
            <li>Industry Profile assigned: <strong>{a.industry_profile_key ? 'yes' : 'no'}</strong></li>
            <li>Legal policy assigned: <strong>{a.legal_policy_key ? 'yes' : 'no'}</strong></li>
            <li>Legal policy published: <strong>{a.lifecycle === 'active' ? 'yes' : 'no'}</strong></li>
            <li>Applicability confirmed: <strong>{a.applicability_confirmed ? 'yes' : 'no'}</strong></li>
          </ul>

          {/* CAPABILITY READINESS — rendered verbatim from the canonical commercial feature
              projection. This panel derives nothing: it has no mapping from a release-state string
              to a meaning, because Commercial Control already owns that decision. An earlier version
              stated this posture in fixed copy and went stale the moment the catalogue was synced. */}
          <div className="mt-3 pt-3 border-t">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
              Capability (Commercial Control)
            </div>
            {!capKnown ? (
              <p className="text-sm text-gray-600">
                Retention governance does not appear in this tenant&apos;s feature projection.
              </p>
            ) : (
              <ul className="text-sm space-y-1">
                <li>Capability: <span className="font-mono text-xs">{cap.feature_key}</span>
                  {cap.feature_display_name ? ` — ${cap.feature_display_name}` : ''}</li>
                <li>Release state: <strong>{cap.release_state || '—'}</strong></li>
                <li>Business plane state: <strong>{cap.business_plane_state || '—'}</strong></li>
                <li>Usable: <strong>{cap.usable ? 'yes' : 'no'}</strong></li>
                <li>Customer visible: <strong>{cap.customer_visible ? 'yes' : 'no'}</strong></li>
                <li>Entitled: <strong>{cap.entitled ? 'yes' : 'no'}</strong></li>
                {cap.capability_source
                  ? <li>Capability source: <strong>{cap.capability_source}</strong></li> : null}
              </ul>
            )}
            <p className="text-xs text-gray-600 mt-2">
              Availability is decided by Commercial Control and the feature catalogue. Assigning a
              legal policy here is governed configuration; it does not by itself make the capability
              operational, and this panel reports whatever that authority currently says.
            </p>
          </div>
        </section>

        {/* ── ASSIGN ─────────────────────────────────────────────────────────────────────────── */}
        <section className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-gray-500">Assign a published policy</div>
          <select
            className="border rounded px-2 py-1 w-full"
            value={selected}
            onChange={(e) => { setSelected(e.target.value); setPreview(null); setConfirmed(false); }}
          >
            <option value="">Select a published policy version…</option>
            {policies.map((p) => (
              <option key={p.id} value={`${p.policy_key}@${p.version}`}>
                {(p.display_name || p.policy_key)} v{p.version}
                {[p.country, p.region].filter(Boolean).length
                  ? ` — ${[p.country, p.region].filter(Boolean).join(' / ')}` : ''}
              </option>
            ))}
          </select>
          {policies.length === 0 && (
            <p className="text-sm text-gray-600">
              No published legal policies exist yet. Author and publish one on the Legal /
              Jurisdiction Policies surface first.
            </p>
          )}

          <Button variant="outline" disabled={!selected || busy} onClick={doPreview}>Preview</Button>

          {preview && (
            <div className="border rounded p-3 bg-gray-50 text-sm space-y-1">
              <div>
                {preview.from_policy_key || '— none —'} v{preview.from_version ?? '—'}
                {' → '}
                <strong>{preview.to_policy_key} v{preview.to_version}</strong>
                {preview.no_change ? ' (no change)' : ''}
              </div>
              <div>
                Assignable: <strong>{preview.assignable ? 'yes' : 'no'}</strong>
                {preview.blocked_reason ? ` — ${preview.blocked_reason}` : ''}
              </div>
              <div className="text-gray-600">
                Review: {preview.review_status || 'not recorded'}
                {preview.reviewed_by
                  ? ` — ${preview.reviewed_by} (${preview.reviewer_role}, ${preview.reviewer_organization})`
                  : ''}
                {' · '}{preview.source_count ?? 0} source authorit{(preview.source_count === 1) ? 'y' : 'ies'}
              </div>
              {delta && (
                <div className="text-gray-600">
                  Retention values — added: {arr(delta.added).join(', ') || 'none'} · removed:{' '}
                  {arr(delta.removed).join(', ') || 'none'} · changed:{' '}
                  {arr(delta.changed).join(', ') || 'none'}
                </div>
              )}
              <div className="text-gray-600">
                Industry Profile unchanged by this assignment:{' '}
                <strong>{preview.industry_profile_unchanged ? 'yes' : 'no'}</strong>
              </div>
            </div>
          )}

          {preview?.assignable && (
            <div className="border rounded p-3 space-y-2">
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-1" checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)} />
                <span>
                  I confirm this published policy is applicable to this organisation. This records the
                  custodian&apos;s acknowledgement; it does not edit the policy, and it makes no
                  representation that the values are legally correct.
                </span>
              </label>
              <div className="grid md:grid-cols-2 gap-2">
                <label className="text-sm">
                  <span className="block text-gray-600">confirmed by</span>
                  <input className="border rounded px-2 py-1 w-full" value={confirmedBy}
                    onChange={(e) => setConfirmedBy(e.target.value)} />
                </label>
                <label className="text-sm">
                  <span className="block text-gray-600">effective from (optional)</span>
                  <input className="border rounded px-2 py-1 w-full" value={effectiveFrom}
                    onChange={(e) => setEffectiveFrom(e.target.value)} placeholder="YYYY-MM-DD" />
                </label>
              </div>
              <Button disabled={!confirmed || busy} onClick={doApply}>Apply assignment</Button>
            </div>
          )}

          {result && (
            <div className="text-sm text-green-700">
              Applied{result.no_change ? ' (no change)' : ''}. Canonical state re-read above.
            </div>
          )}
          {err && <div className="text-sm text-red-700">{err}</div>}
        </section>
      </CardContent>
    </Card>
  );
};

export default Tenant360LegalPolicy;
